import { useState, useEffect, useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import "./index.css";
import { HomePage } from "./pages/HomePage";
import { CreatePage } from "./pages/CreatePage";
import { ConvertPage } from "./pages/ConvertPage";
import { convertPairForPath } from "./convertPairs";
import { PreviewPage } from "./pages/PreviewPage";
import { ReplayPage } from "./pages/ReplayPage";
import { ReplayUploadPage } from "./pages/ReplayUploadPage";
import { DigitizePage } from "./pages/DigitizePage";
import type { Density, DigitizeResult } from "./pages/DigitizePage";
import { StudioLink } from "./components/StudioLink";
import { STUDIO_URL } from "./studio";
import { DevFlagsPage } from "./pages/DevFlagsPage";
import { AdminPage } from "./pages/AdminPage";
import { AdminEmailPage } from "./pages/AdminEmailPage";
import { AdminCampaignsPage } from "./pages/AdminCampaignsPage";
import { AdminMediaPage } from "./pages/AdminMediaPage";
import { AdminTemplatesPage } from "./pages/AdminTemplatesPage";
import { StudioPage } from "./pages/StudioPage";
import { PayPage } from "./pages/PayPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import { DigitizingServicePage } from "./pages/DigitizingServicePage";
import type { DigitizingServiceConfig } from "./pages/DigitizingServicePage";
import { DigitizingServiceBanner } from "./components/DigitizingServiceBanner";
import { LoginPage } from "./pages/LoginPage";
import type { LoginMethod, LoginResult } from "./pages/LoginPage";
import { AccountSummary, ProfileMenu } from "./components/ProfileMenu";
import { SiteFooter } from "./components/SiteFooter";
import { SiteHeader } from "./components/SiteHeader";
import { SaveAccountModal } from "./components/SaveAccountModal";
import { LoggedInModal } from "./components/LoggedInModal";
import { clearStoredEmail, setStoredEmail } from "./emailStorage";
import { WITH_COOKIE, forgetLegacyToken, legacyToken } from "./tokenStorage";
import { useBackendVersionChanged } from "./versionCheck";

// Same-origin in production, where Caddy proxies /api, /fonts and /assets to
// the backend. In local dev the backend is a separate process on 5555.
//
// Deliberately not process.env.NODE_ENV: that only works if the bundler
// substitutes it, which varies by Bun version and left `process is not
// defined` in the browser on 1.3.14. Reading the hostname needs no build-time
// substitution and cannot throw.
//
// Note the failure mode if this is ever got wrong: falling back to "" in dev
// is a broken local setup, whereas falling back to localhost:5555 in
// production is a broken production site - so the check identifies dev, and
// production is what you get by default.
const isLocalDev = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_URL = isLocalDev ? "http://localhost:5555" : "";

interface Font {
  name: string;
  preview: string;
  has_preview?: boolean;
}

interface AssetFile {
  [format: string]: string;
}

interface Asset {
  id: string;
  content_key: string;
  text: string;
  font: string;
  width_inches: number;
  height_inches: number;
  created_at: string;
  files: AssetFile;
  has_embroidery: boolean;
  is_unlocked: boolean;
  // Which tool produced this design -- see app/routers/digitize.py's own
  // ENGINE comment. Every page that renders pastAssets filters on this.
  engine: string;
}

type PageType = 'home' | 'create' | 'convert' | 'preview' | 'replay' | 'replayUpload' | 'digitize' | 'editor' | 'devflags' | 'login' | 'digitizingService' | 'admin' | 'adminEmail' | 'adminCampaigns' | 'adminMedia' | 'adminTemplates' | 'studio' | 'pay' | 'unsubscribe';

// Pure mirror of the path-matching in initializeToken's effect below --
// deliberately duplicated rather than shared, because this one has to stay
// synchronous. currentPage used to start at 'home' unconditionally and only
// get corrected once that effect's async token lookup resolved, so every
// non-home route rendered as the home page for however long that fetch
// took -- indefinitely for a renderer whose request to /api/link/* never
// completes (Googlebot's render of /create showed exactly this: the home
// page, because /api/ is disallowed in robots.txt). This computes the
// right page from the URL alone, with no network round trip, so the very
// first render already shows it. The async effect still runs after mount
// to resolve/validate the token and re-set currentPage -- for /create that
// second call is now redundant with this one rather than the only source
// of truth, and for a URL that fails validation it still correctly moves
// off of 'create' via createNewToken.
// The custom digitizing service's quote form. A human service, unrelated to
// the automatic /digitize tool.
const DIGITIZING_SERVICE_PATH = '/digitizing-service';
// Stitch Studio's landing page. /studiomobile is where Studio's own phone
// notice links (embroidery-desktop), so it serves the same page at its own
// URL rather than redirecting: see StudioPage.tsx.
const STUDIO_PATH = '/studio';
const STUDIO_MOBILE_PATH = '/studiomobile';

// A custom order's payment page (PayPage.tsx). The id is CustomOrder.id, a
// uuid4 hex.
const PAY_PATH = /^\/pay\/([a-f0-9]{32})$/;

// A campaign email's unsubscribe link (UnsubscribePage.tsx). The token is
// EmailCampaignRecipient.unsubscribe_token, URL-safe base64, or "test".
const UNSUBSCRIBE_PATH = /^\/unsubscribe\/([A-Za-z0-9_-]{1,64})$/;

function pageForPath(path: string): PageType {
  if (path === '/dev/flags') return 'devflags';
  if (path === '/login') return 'login';
  if (path === '/admin') return 'admin';
  if (path === '/admin/email') return 'adminEmail';
  if (path === '/admin/campaigns') return 'adminCampaigns';
  if (path === '/admin/media') return 'adminMedia';
  if (path === '/admin/templates') return 'adminTemplates';
  if (PAY_PATH.test(path)) return 'pay';
  if (UNSUBSCRIBE_PATH.test(path)) return 'unsubscribe';
  if (path === DIGITIZING_SERVICE_PATH) return 'digitizingService';
  if (path === STUDIO_PATH || path === STUDIO_MOBILE_PATH) return 'studio';
  if (path === '/convert') return 'convert';
  if (convertPairForPath(path)) return 'convert';
  if (path === '/preview') return 'preview';
  if (path === '/digitize') return 'digitize';
  if (path === '/editor') return 'editor';
  if (path === '/replay') return 'replayUpload';
  if (/^\/replay\/content\/[a-f0-9]+$/.test(path)) return 'replay';
  if (/^\/replay\/[a-f0-9]+$/.test(path)) return 'replay';
  if (/^\/create\/[a-f0-9]+$/.test(path)) return 'create';
  if (path === '/create') return 'create';
  return 'home';
}

export function App() {
  // Picks up a deploy in a tab that stays open across it; see versionCheck.ts.
  const newVersion = useBackendVersionChanged(API_URL);
  const [newVersionDismissed, setNewVersionDismissed] = useState(false);
  const [currentPage, setCurrentPage] = useState<PageType>(() => pageForPath(window.location.pathname));
  // Which format pair /convert/{slug} is set up for (see convertPairs.ts),
  // or undefined on the generic /convert. Read once from the URL: the only
  // in-app way between the two is the nav, which clears it below; the
  // pages' own links to each other are plain full-page loads.
  const [convertPair, setConvertPair] = useState(() => convertPairForPath(window.location.pathname));
  const [token, setToken] = useState<string>("");
  // Whether initializeToken below has finished deciding which token this
  // visit uses -- the stored one, a freshly minted one, or none at all.
  // Flags wait on it so they are evaluated once, for the right viewer,
  // rather than first as 'anonymous' and then again for the real token.
  const [tokenResolved, setTokenResolved] = useState(false);
  // DigitizePage's own form + result, lifted here for the same reason
  // replayAssetId/replayContentKey live here rather than in ReplayPage: the
  // page below fully unmounts on every navigation away, which would
  // otherwise discard a picked file, chosen settings, and a finished result
  // the moment someone so much as checked another page.
  const [digitizeFile, setDigitizeFile] = useState<File | null>(null);
  const [digitizeDensity, setDigitizeDensity] = useState<Density>("standard");
  const [digitizeWidth, setDigitizeWidth] = useState("");
  const [digitizeHeight, setDigitizeHeight] = useState("");
  const [digitizeResult, setDigitizeResult] = useState<DigitizeResult | null>(null);
  // Which design /replay is showing. Only meaningful when currentPage is
  // 'replay' -- unlike token, this is set purely from the URL and reset by
  // simply navigating away, so there is no localStorage/minting story here.
  // Exactly one of these is ever set, by the two /replay routes below: a
  // design generated on /create (assetId) or one uploaded through /convert
  // (replayContentKey), which has no GeneratedAsset row to have an id.
  const [replayAssetId, setReplayAssetId] = useState<string>("");
  const [replayContentKey, setReplayContentKey] = useState<string>("");
  const [pastAssets, setPastAssets] = useState<Asset[]>([]);
  const [fonts, setFonts] = useState<Font[]>([]);
  // The header's link list collapses behind a toggle on phones (see the
  // .nav-toggle styles in index.css); on wider screens this is ignored.
  const [navOpen, setNavOpen] = useState(false);
  // The email on file for the current token, as /api/link/validate reports
  // it, for the header's profile menu. Empty means "Anonymous".
  const [profileEmail, setProfileEmail] = useState("");
  // The picture a Google login left on the account; empty for the blank
  // silhouette.
  const [profileAvatar, setProfileAvatar] = useState("");
  const [saveAccountOpen, setSaveAccountOpen] = useState(false);
  const [loggedInOpen, setLoggedInOpen] = useState(false);
  // Stable, so the modal's auto-close timer isn't restarted by every render.
  const closeLoggedIn = useCallback(() => setLoggedInOpen(false), []);

  // Flags the browser gates on, evaluated by the backend for this token.
  // Defaulted true (not {}) for every flag that's actually on for everyone
  // in prod today (see SITEMAP_PATHS's comment in index.ts) -- these only
  // gate already-public frontend UI, so there's nothing lost by rendering
  // as if they're on before /api/flags responds. The real fetch below
  // overwrites this once it resolves; if it never does (a slow connection,
  // or a renderer -- Googlebot's included -- that doesn't wait for it), the
  // page now shows real content instead of rendering null forever, which is
  // what left /convert and friends showing an empty body in Search Console.
  // A flag meant to default off (like "digitizer") is simply left out here,
  // keeping its old flash-free unresolved-until-fetched behavior.
  // "profiles" is one of those: the header avatar, the phone menu's Account
  // section, the save-account dialog and /login all appear only once the
  // fetch says so.
  // Whether /api/flags has answered, or failed to. Until then a flag left
  // out of the defaults below is unknown, and its gated page neither shows
  // nor redirects; after, unknown means off.
  const [flagsSettled, setFlagsSettled] = useState(false);
  const [flags, setFlags] = useState<Record<string, boolean | string>>({
    email_capture: true,
    format_converter: true,
    stitch_replayer: true,
    embroidery_viewer: true,
    svg_digitizer: false,
  });

  // Undefined when the app renders without a PostHogProvider, i.e. when
  // BUN_PUBLIC_POSTHOG_KEY is unset (local dev). Call sites must guard.
  const posthog = usePostHog();

  // The authorized-link token is the only identity this app has, so it is the
  // distinct_id. Identifying rather than attaching it as a property to each
  // capture means autocaptured events carry it too, and the anonymous events
  // from before the token resolved get stitched onto the same person.
  //
  // This is also the id the backend already evaluates feature flags against
  // (ViewerContext in app/routers/generate.py), so flag exposures and product
  // events land on one person rather than two.
  useEffect(() => {
    if (!posthog || !token) return;
    posthog.identify(token);
  }, [posthog, token]);

  // Escape closes the mobile menu, as it would any other disclosure that
  // covers part of the page.
  useEffect(() => {
    if (!navOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navOpen]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      window.location.reload();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Initialize token from URL or the token cookie
  useEffect(() => {
    const initializeToken = async () => {
      const path = window.location.pathname;

      // Before any token handling: this page needs no token, and falling
      // through would mint one and redirect to /create. A token this browser
      // already has is still adopted, so flags are evaluated for it.
      if (path === '/dev/flags') {
        setCurrentPage('devflags');
        await ensureToken(false);
        return;
      }

      // Never mints: a token made here would only be thrown away by the
      // login. One this browser already has is adopted, so an anonymous
      // account can be kept under the email it logs in with.
      if (path === '/login') {
        setCurrentPage('login');
        await ensureToken(false);
        return;
      }

      // Never mints either: only a logged-in admin's token gets past the
      // API, and a fresh one never is.
      // A customer paying a quote: no reason to mint them a link.
      if (PAY_PATH.test(path)) {
        setCurrentPage('pay');
        await ensureToken(false);
        return;
      }

      // Nor someone unsubscribing from an email.
      if (UNSUBSCRIBE_PATH.test(path)) {
        setCurrentPage('unsubscribe');
        await ensureToken(false);
        return;
      }

      if (path === '/admin' || path === '/admin/email' || path === '/admin/campaigns' || path === '/admin/media' || path === '/admin/templates') {
        setCurrentPage(pageForPath(path));
        await ensureToken(false);
        return;
      }

      // Nor is reading about Studio, or asking for its link by email.
      if (path === STUDIO_PATH || path === STUDIO_MOBILE_PATH) {
        setCurrentPage('studio');
        await ensureToken(false);
        return;
      }

      // Works logged out, and asking for a quote is no reason to mint a link.
      if (path === DIGITIZING_SERVICE_PATH) {
        setCurrentPage('digitizingService');
        await ensureToken(false);
        return;
      }

      // Operates on an uploaded file, not a design tied to an account, so it
      // works with no token at all -- but ensureToken still mints/reuses one
      // in the background, so a conversion made here can still be emailed or
      // offered Pro afterward, through the same plumbing /create uses.
      if (path === '/convert' || convertPairForPath(path)) {
        setCurrentPage('convert');
        await ensureToken();
        return;
      }

      // Same reasoning as /convert: an uploaded file, no account involved.
      if (path === '/preview') {
        setCurrentPage('preview');
        await ensureToken(false);
        return;
      }

      // Same reasoning as /convert: an uploaded file, no account involved,
      // but ensureToken still mints/reuses one in the background so a
      // digitized design can show up in this token's library and be
      // emailed/Pro-upsold afterward, through the same plumbing.
      if (path === '/digitize') {
        setCurrentPage('digitize');
        await ensureToken();
        return;
      }

      // The bare replayer entry point: upload a file, watch it. Checked
      // before the two /replay/... routes below since neither of those
      // regexes matches an empty remainder anyway, but the distinction is
      // worth being explicit about -- this one needs no id at all. Same
      // ensureToken reasoning as /convert above: an upload here goes through
      // the same /api/convert call.
      // Same reasoning as /convert: every design dropped on the canvas goes
      // through /api/convert, which wants a token to file it in the library.
      if (path === '/editor') {
        setCurrentPage('editor');
        await ensureToken();
        return;
      }

      if (path === '/replay') {
        setCurrentPage('replayUpload');
        await ensureToken();
        return;
      }

      // Checked before the assetId route below: both match [a-f0-9]+ at that
      // position, but this one has a literal "content" segment first, so
      // there's no real ambiguity -- ordered this way just so the more
      // specific pattern doesn't have to rely on that being obvious.
      const replayContentMatch = path.match(/^\/replay\/content\/([a-f0-9]+)$/);
      if (replayContentMatch && replayContentMatch[1]) {
        setReplayContentKey(replayContentMatch[1]);
        setCurrentPage('replay');
        await ensureToken();
        return;
      }

      // The asset id in the URL is what /api/pointcloud reads, the same way
      // an /assets/media URL needs no token to resolve -- but ensureToken
      // still mints/reuses one, the same reasoning as /convert above: without
      // it there is no token to email this design's files to.
      const replayMatch = path.match(/^\/replay\/([a-f0-9]+)$/);
      if (replayMatch && replayMatch[1]) {
        setReplayAssetId(replayMatch[1]);
        setCurrentPage('replay');
        await ensureToken();
        return;
      }

      // Parse token from URL: /create/{token}. Only reachable from a link
      // minted before the token moved to browser storage only -- old bookmarks
      // and anything already shared still resolve, but the token is a bearer
      // credential and doesn't belong in the address bar (or in analytics
      // that capture full URLs), so it's stripped back to bare /create as
      // soon as it's adopted rather than left sitting in the location bar.
      const match = path.match(/^\/create\/([a-f0-9]+)$/);
      if (match && match[1]) {
        const { token: resolved, stale, failed } = await resolveToken(match[1]);
        if (resolved) {
          setToken(resolved);
          window.history.replaceState({}, '', '/create');
          setCurrentPage('create');
        } else if (!failed) {
          await createNewToken(stale);
        }
        return;
      }

      if (path === '/create') {
        const { token: resolved, stale, failed } = await resolveToken();
        if (resolved) {
          setToken(resolved);
          setCurrentPage('create');
        } else if (failed) {
          setCurrentPage('create');
        } else {
          await createNewToken(stale);
        }
        return;
      }

      // Home never mints a token, but one this browser already has is the
      // same visitor's, so it's adopted rather than left as 'anonymous'.
      setCurrentPage('home');
      await ensureToken(false);
    };

    initializeToken().finally(() => setTokenResolved(true));
  }, []);

  // Asks the server which link this browser is. The token lives in an
  // HttpOnly cookie the page can't read, so this response is how the page
  // learns it. `offered` is tried ahead of the cookie: a /create/{token}
  // link, or a token the pre-cookie frontend left in localStorage (see
  // tokenStorage.ts), which the server then moves into the cookie.
  //
  // `stale`: a token existed but none still resolves, so whatever is minted
  // next replaces an old identity. `failed`: no answer at all. Callers must
  // not mint then -- minting rewrites the cookie, and the visitor's real
  // link may be sitting in it, unreachable only for the moment.
  const resolveToken = async (
    offered: string | null = legacyToken(),
  ): Promise<{ token: string; stale: boolean; failed: boolean }> => {
    try {
      // The token goes in the body, not the URL, like /api/generate: it is a
      // bearer credential, and paths end up in logs and history.
      const response = await fetch(`${API_URL}/api/link/validate`, {
        ...WITH_COOKIE,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: offered }),
      });
      if (!response.ok) {
        console.warn("Token validation failed, assuming valid");
        return { token: offered || '', stale: false, failed: true };
      }
      const data = await response.json();
      // Every caller adopts a token that validates, so its email is the
      // profile's. One that doesn't is about to be replaced by a fresh link,
      // which has no email yet.
      setProfileEmail(data.valid && typeof data.email === 'string' ? data.email : '');
      setProfileAvatar(data.valid && typeof data.avatar_url === 'string' ? data.avatar_url : '');
      if (!data.valid) return { token: '', stale: Boolean(data.stale), failed: false };
      forgetLegacyToken();
      return { token: data.token, stale: false, failed: false };
    } catch (err) {
      console.warn("Failed to validate token, assuming valid", err);
      return { token: offered || '', stale: false, failed: true };
    }
  };

  // Same lazy mint-or-reuse logic as the /create branch above, minus the
  // navigation and past-assets fetch: those routes redirect to /create and
  // populate its library, neither of which applies to a page that stays put
  // on its own URL. Used by /convert and /replay so a design made there can
  // still be emailed or offered Pro afterward, without those pages minting
  // a token from a bare page load the way /create intentionally does.
  // `mint` false only adopts a stored, still-valid token: what home, /preview
  // and /dev/flags use, so a returning visitor is the same viewer there
  // without a first visit to them creating a link.
  const ensureToken = async (mint = true) => {
    const { token: resolved, failed } = await resolveToken();
    if (resolved) {
      setToken(resolved);
      return;
    }
    if (!mint || failed) return;
    try {
      const response = await fetch(`${API_URL}/api/link/create`, { ...WITH_COOKIE, method: 'POST' });
      const data = await response.json();
      setToken(data.token);
      forgetLegacyToken();
    } catch (err) {
      console.error("Failed to create access token", err);
    }
  };

  // `replacesToken` says a token we already had stopped validating. That is
  // this app's logout: the new link is a different person, so PostHog has to
  // drop the old distinct_id or every link the user ever burns through folds
  // into one immortal person. It is false for a first-time visitor, where
  // there is no old identity to shed and resetting would only throw away the
  // anonymous events that identify() is about to stitch on.
  const createNewToken = async (replacesToken = false) => {
    try {
      const response = await fetch(`${API_URL}/api/link/create`, { ...WITH_COOKIE, method: 'POST' });
      const data = await response.json();
      const newToken = data.token;
      if (replacesToken) posthog?.reset();
      setToken(newToken);
      forgetLegacyToken();
      // Covers the invalid-url-token path: the address bar may still show a
      // dead /create/{token} from before this call. A no-op if we're already
      // on bare /create.
      window.history.replaceState({}, '', '/create');
      setCurrentPage('create');
      fetchPastAssets(newToken);
    } catch (err) {
      console.error("Failed to create access token", err);
    }
  };

  useEffect(() => {
    // The library is shared across every tool a token has used (see
    // GeneratedDesign.engine), fetched here once and filtered per-page --
    // 'digitize' included now that it also writes GeneratedAsset rows.
    if (token && (currentPage === 'create' || currentPage === 'digitize')) {
      fetchPastAssets(token);
    }
  }, [token, currentPage]);

  // Flags gate the nav entry too, so they're needed even when no token exists
  // (a first visit to '/' never mints one). 'anonymous' is a stand-in viewer
  // id, not a real token -- /api/flags evaluates it without looking
  // it up, unlike the token-scoped endpoints. It is only used once
  // initializeToken has settled on there being no token: a browser that has
  // one gets its flags evaluated for it on every page, not as 'anonymous'.
  useEffect(() => {
    if (!tokenResolved) return;
    fetchFlags(token || 'anonymous');
  }, [token, tokenResolved]);

  // What the digitizing banner and page may say (price, turnaround, upload
  // limit). Only asked for once the flag is on; the endpoint 404s otherwise,
  // which is the server's own check on the same flag.
  const [digitizingConfig, setDigitizingConfig] = useState<DigitizingServiceConfig | null>(null);
  const [digitizingConfigFailed, setDigitizingConfigFailed] = useState(false);
  useEffect(() => {
    if (flags.digitizing_service !== true) return;
    setDigitizingConfigFailed(false);
    fetch(`${API_URL}/api/digitizing-service/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token || 'anonymous' }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => (data ? setDigitizingConfig(data) : setDigitizingConfigFailed(true)))
      .catch((err) => {
        console.warn("Failed to load digitizing service config", err);
        setDigitizingConfigFailed(true);
      });
  }, [flags.digitizing_service, token]);

  // Every entry point to the custom digitizing service -- banner, home card,
  // the page itself -- shows only on this. Fails closed: the flag still
  // loading, its request failing, or the flag missing from the answer all
  // read as off, and the config has to have loaded too, so nothing appears
  // and then changes its copy once the turnaround arrives.
  const digitizingServiceOn = flags.digitizing_service === true && digitizingConfig !== null;

  // The old in-site editor is gone; Stitch Studio replaced it. Old links and
  // bookmarks to /editor land there instead of on a dead page. replace, not
  // assign, so Back skips the /editor hop.
  useEffect(() => {
    if (currentPage === 'editor') window.location.replace(STUDIO_URL);
  }, [currentPage]);

  // With the service off the page doesn't exist: send the visitor home, the
  // way the other gated pages would say "not available", but with nothing
  // to say about a service that isn't offered. Waits for the flags to
  // settle, so the page isn't left before it has had the chance to show.
  useEffect(() => {
    if (currentPage !== 'digitizingService') return;
    const off =
      (flagsSettled && flags.digitizing_service !== true) ||
      (flags.digitizing_service === true && digitizingConfigFailed);
    if (off) {
      window.history.replaceState({}, '', '/');
      setCurrentPage('home');
    }
  }, [currentPage, flagsSettled, flags.digitizing_service, digitizingConfigFailed]);

  // `from` is where the visitor came from, for the page's
  // digitizing_page_viewed event; the page reads it and drops it from the URL.
  const goToDigitizingService = (from: 'banner' | 'home' | 'viewer') => {
    setNavOpen(false);
    window.history.pushState({}, '', `${DIGITIZING_SERVICE_PATH}?from=${from}`);
    setCurrentPage('digitizingService');
    window.scrollTo({ top: 0 });
  };

  const fetchFlags = async (tok: string) => {
    try {
      const response = await fetch(`${API_URL}/api/flags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tok }),
      });
      if (!response.ok) return;
      setFlags(await response.json());
    } catch (err) {
      // Flags failing closed is the safe direction: the site works without
      // the features they gate, so this stays quiet rather than erroring.
      console.warn("Failed to load feature flags", err);
    } finally {
      setFlagsSettled(true);
    }
  };

  // Adopt the account a login opened, then go to its library. A login into a
  // different account than this browser had is a change of person, so
  // PostHog drops the old identity, as createNewToken does.
  const handleLoggedIn = (result: LoginResult, method: LoginMethod) => {
    const switchedAccount = Boolean(token) && token !== result.token;
    if (switchedAccount) posthog?.reset();
    // Identified here, ahead of the effect that would do it on the token
    // change, so the login lands on the account it opened rather than on
    // the person reset() just dropped.
    posthog?.identify(result.token);
    posthog?.capture("user_logged_in", {
      method,
      had_token: Boolean(token),
      switched_account: switchedAccount,
    });
    // The login response already set the cookie to this account.
    setToken(result.token);
    forgetLegacyToken();
    setProfileEmail(result.email || '');
    setProfileAvatar(result.avatar_url || '');
    if (result.email) setStoredEmail(result.email);
    window.history.pushState({}, '', '/create');
    setCurrentPage('create');
    fetchPastAssets(result.token);
    setLoggedInOpen(true);
  };

  // Forgets this browser's token and everything shown for it. The account
  // itself is untouched, there for the next login. The cookie is HttpOnly,
  // so only the server can clear it -- awaited, so a link minted right after
  // (say, by clicking Create) can't be wiped by a late clear.
  const handleLogOut = async () => {
    try {
      await fetch(`${API_URL}/api/link/logout`, { ...WITH_COOKIE, method: 'POST' });
    } catch (err) {
      console.warn("Failed to clear the token cookie", err);
    }
    forgetLegacyToken();
    clearStoredEmail();
    posthog?.reset();
    setToken('');
    setProfileEmail('');
    setProfileAvatar('');
    setPastAssets([]);
    setNavOpen(false);
    navigateTo('home');
  };

  const goToLogin = () => {
    setNavOpen(false);
    window.history.pushState({}, '', '/login');
    setCurrentPage('login');
  };

  const openSaveAccount = () => {
    setNavOpen(false);
    setSaveAccountOpen(true);
  };

  const fetchPastAssets = async (tok: string) => {
    try {
      const response = await fetch(`${API_URL}/api/${tok}/assets`);
      const data = await response.json();
      setPastAssets(data.assets || []);
    } catch (err) {
      console.error("Failed to fetch past assets", err);
    }
  };

  // Fetch fonts
  useEffect(() => {
    fetch(`${API_URL}/fonts`)
      .then(res => res.json())
      .then((data: Font[]) => {
        setFonts(data);
      })
      .catch((err) => console.error("Failed to load fonts", err));
  }, []);

  const navigateTo = async (page: PageType) => {
    if (page === 'create') {
      const { token: resolved, stale, failed } = await resolveToken();
      if (resolved) {
        window.history.pushState({}, '', '/create');
        setToken(resolved);
        setCurrentPage('create');
        fetchPastAssets(resolved);
      } else if (failed) {
        window.history.pushState({}, '', '/create');
        setCurrentPage('create');
      } else {
        await createNewToken(stale);
      }
    } else if (page === 'convert') {
      window.history.pushState({}, '', '/convert');
      setConvertPair(undefined);
      setCurrentPage('convert');
    } else if (page === 'preview') {
      window.history.pushState({}, '', '/preview');
      setCurrentPage('preview');
    } else if (page === 'digitize') {
      window.history.pushState({}, '', '/digitize');
      setCurrentPage('digitize');
      // Unlike convert/preview/replayUpload here, digitize can't just degrade
      // quietly without a token -- /api/digitize requires one (see that
      // router's own module docstring). The initial-page-load path (a
      // reload or direct link to /digitize) already calls this; this is the
      // other way to arrive here -- clicking the nav entry from wherever
      // someone already was -- which was missing it entirely, so anyone who
      // landed on Home first and clicked through got an empty token and a
      // permanently stuck "Still setting things up" on every submit.
      ensureToken();
    } else if (page === 'editor') {
      window.history.pushState({}, '', '/editor');
      setCurrentPage('editor');
      ensureToken();
    } else if (page === 'replayUpload') {
      window.history.pushState({}, '', '/replay');
      setCurrentPage('replayUpload');
    } else {
      window.history.pushState({}, '', '/');
      setCurrentPage('home');
    }
  };

  const handleLoadAsset = () => {
    // Refresh data when an asset is loaded
    fetchPastAssets(token);
  };

  return (
    <>
      {newVersion && !newVersionDismissed && (
        <div className="update-banner" role="status">
          <span>A new version of the site is available.</span>
          <button type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
          <button
            type="button"
            className="update-banner-dismiss"
            aria-label="Dismiss"
            onClick={() => setNewVersionDismissed(true)}
          >
            ×
          </button>
        </div>
      )}
      <DigitizingServiceBanner
        enabled={digitizingServiceOn}
        page={currentPage}
        suppressed={currentPage === 'digitizingService' || currentPage === 'login' || currentPage === 'devflags'}
        priceFrom={digitizingConfig?.price_from ?? null}
        turnaround={digitizingConfig?.turnaround ?? null}
        snoozeHours={digitizingConfig?.banner_snooze_hours ?? 24}
        onGetQuote={() => goToDigitizingService('banner')}
      />
      <SiteHeader
        flags={flags}
        // A pair page (/convert/pes-to-dst) is not the generic converter the
        // "Convert Files" entry names, so that one stays a link there.
        currentPage={convertPair ? null : currentPage}
        navOpen={navOpen}
        onNavigate={(page) => {
          setNavOpen(false);
          navigateTo(page as PageType);
        }}
        onToggleNav={() => setNavOpen((open) => !open)}
        profileMenu={flags.profiles === true && (
          <ProfileMenu
            email={profileEmail}
            avatarUrl={profileAvatar}
            onLogIn={goToLogin}
            onLogOut={handleLogOut}
            onAddEmail={openSaveAccount}
          />
        )}
        accountSummary={flags.profiles === true && (
          <AccountSummary
            email={profileEmail}
            avatarUrl={profileAvatar}
            onLogIn={goToLogin}
            onLogOut={handleLogOut}
            onAddEmail={openSaveAccount}
          />
        )}
        renderStudioLink={(children) => <StudioLink source="nav">{children}</StudioLink>}
      />

      <main className="container">
        {currentPage === 'home' && (
          <HomePage
            onNavigateToCreate={() => navigateTo('create')}
            onNavigateToConvert={() => navigateTo('convert')}
            onNavigateToPreview={() => navigateTo('preview')}
            onNavigateToDigitize={() => navigateTo('digitize')}
            formatConverterEnabled={flags.format_converter === true}
            stitchReplayerEnabled={flags.stitch_replayer === true}
            embroideryViewerEnabled={flags.embroidery_viewer === true}
            svgDigitizerEnabled={flags.svg_digitizer === true}
            onNavigateToDigitizingService={() => goToDigitizingService('home')}
            digitizingServiceEnabled={digitizingServiceOn}
            digitizingTurnaround={digitizingConfig?.turnaround ?? null}
          />
        )}

        {currentPage === 'studio' && (
          <StudioPage apiUrl={API_URL} fromMobile={window.location.pathname === STUDIO_MOBILE_PATH} />
        )}

        {currentPage === 'digitizingService' && digitizingServiceOn && (
          <DigitizingServicePage apiUrl={API_URL} token={token} config={digitizingConfig} />
        )}

        {currentPage === 'devflags' && (
          <DevFlagsPage apiUrl={API_URL} />
        )}

        {currentPage === 'pay' && (
          <PayPage apiUrl={API_URL} orderId={window.location.pathname.match(PAY_PATH)?.[1] ?? ''} />
        )}

        {currentPage === 'unsubscribe' && (
          <UnsubscribePage apiUrl={API_URL} token={window.location.pathname.match(UNSUBSCRIBE_PATH)?.[1] ?? ''} />
        )}

        {currentPage === 'adminCampaigns' && (
          <AdminCampaignsPage apiUrl={API_URL} token={token} tokenResolved={tokenResolved} />
        )}
        {currentPage === 'adminMedia' && (
          <AdminMediaPage apiUrl={API_URL} token={token} tokenResolved={tokenResolved} />
        )}
        {currentPage === 'adminTemplates' && (
          <AdminTemplatesPage apiUrl={API_URL} token={token} tokenResolved={tokenResolved} />
        )}

        {currentPage === 'adminEmail' && (
          <AdminEmailPage apiUrl={API_URL} token={token} tokenResolved={tokenResolved} />
        )}

        {currentPage === 'admin' && (
          <AdminPage apiUrl={API_URL} token={token} tokenResolved={tokenResolved} />
        )}

        {currentPage === 'login' && (
          flags.profiles === true ? (
            <LoginPage
              apiUrl={API_URL}
              token={token}
              tokenResolved={tokenResolved}
              currentEmail={profileEmail}
              onLoggedIn={handleLoggedIn}
              onLogOut={handleLogOut}
            />
          ) : flags.profiles === false ? (
            <div className="empty-preview">
              <p>This page isn't available right now.</p>
              <small>
                <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                  Back to home
                </a>
              </small>
            </div>
          ) : null /* flag not resolved yet -- render nothing rather than flash the page on then off */
        )}

        {currentPage === 'create' && (
          <CreatePage
            apiUrl={API_URL}
            token={token}
            fonts={fonts}
            pastAssets={pastAssets}
            onLoadAsset={handleLoadAsset}
            emailCaptureEnabled={flags.email_capture === true}
            stitchReplayerEnabled={flags.stitch_replayer === true}
            formatConverterEnabled={flags.format_converter === true}
            embroideryViewerEnabled={flags.embroidery_viewer === true}
            shareButtonsEnabled={flags.share_buttons === true}
            multilineEnabled={flags.multiline_inkstitch === true}
            proUpsellVariant={
              typeof flags["pro-upsell-test"] === "string"
                ? (flags["pro-upsell-test"] as string)
                : "control"
            }
          />
        )}

        {currentPage === 'convert' && (
          flags.format_converter === true ? (
            <ConvertPage
              // Remount on the nav from a pair page to /convert, so the
              // form and any result start fresh along with the copy.
              key={convertPair?.slug ?? ''}
              pair={convertPair}
              apiUrl={API_URL}
              token={token}
              stitchReplayerEnabled={flags.stitch_replayer === true}
              shareButtonsEnabled={flags.share_buttons === true}
              emailCaptureEnabled={flags.email_capture === true}
              embroideryViewerEnabled={flags.embroidery_viewer === true}
              proUpsellVariant={
                typeof flags["pro-upsell-test"] === "string"
                  ? (flags["pro-upsell-test"] as string)
                  : "control"
              }
            />
          ) : flags.format_converter === false ? (
            <div className="empty-preview">
              <p>This page isn't available right now.</p>
              <small>
                <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                  Back to home
                </a>
              </small>
            </div>
          ) : null /* flag not resolved yet -- render nothing rather than flash the page on then off */
        )}

        {currentPage === 'preview' && (
          flags.embroidery_viewer === true ? (
            <PreviewPage
              apiUrl={API_URL}
              formatConverterEnabled={flags.format_converter === true}
              stitchReplayerEnabled={flags.stitch_replayer === true}
              shareButtonsEnabled={flags.share_buttons === true}
              digitizingServiceEnabled={digitizingServiceOn}
              onNavigateToDigitizingService={() => goToDigitizingService('viewer')}
            />
          ) : flags.embroidery_viewer === false ? (
            <div className="empty-preview">
              <p>This page isn't available right now.</p>
              <small>
                <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                  Back to home
                </a>
              </small>
            </div>
          ) : null /* flag not resolved yet -- render nothing rather than flash the page on then off */
        )}

        {currentPage === 'replay' && (
          flags.stitch_replayer === true ? (
            <ReplayPage
              apiUrl={API_URL}
              token={token}
              assetId={replayAssetId || undefined}
              contentKey={replayContentKey || undefined}
              shareButtonsEnabled={flags.share_buttons === true}
              emailCaptureEnabled={flags.email_capture === true}
              proUpsellVariant={
                typeof flags["pro-upsell-test"] === "string"
                  ? (flags["pro-upsell-test"] as string)
                  : "control"
              }
            />
          ) : flags.stitch_replayer === false ? (
            <div className="empty-preview">
              <p>This page isn't available right now.</p>
              <small>
                <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                  Back to home
                </a>
              </small>
            </div>
          ) : null /* flag not resolved yet -- render nothing rather than flash the page on then off */
        )}

        {currentPage === 'digitize' && (
          flags.svg_digitizer === true ? (
            <DigitizePage
              apiUrl={API_URL}
              token={token}
              pastAssets={pastAssets}
              onLoadAsset={handleLoadAsset}
              stitchReplayerEnabled={flags.stitch_replayer === true}
              shareButtonsEnabled={flags.share_buttons === true}
              formatConverterEnabled={flags.format_converter === true}
              emailCaptureEnabled={flags.email_capture === true}
              file={digitizeFile}
              setFile={setDigitizeFile}
              density={digitizeDensity}
              setDensity={setDigitizeDensity}
              width={digitizeWidth}
              setWidth={setDigitizeWidth}
              height={digitizeHeight}
              setHeight={setDigitizeHeight}
              result={digitizeResult}
              setResult={setDigitizeResult}
            />
          ) : flags.svg_digitizer === false ? (
            <div className="empty-preview">
              <p>This page isn't available right now.</p>
              <small>
                <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                  Back to home
                </a>
              </small>
            </div>
          ) : null /* flag not resolved yet -- render nothing rather than flash the page on then off */
        )}

        {currentPage === 'replayUpload' && (
          flags.stitch_replayer === true ? (
            <ReplayUploadPage
              apiUrl={API_URL}
              token={token}
              shareButtonsEnabled={flags.share_buttons === true}
              emailCaptureEnabled={flags.email_capture === true}
              proUpsellVariant={
                typeof flags["pro-upsell-test"] === "string"
                  ? (flags["pro-upsell-test"] as string)
                  : "control"
              }
            />
          ) : flags.stitch_replayer === false ? (
            <div className="empty-preview">
              <p>This page isn't available right now.</p>
              <small>
                <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                  Back to home
                </a>
              </small>
            </div>
          ) : null /* flag not resolved yet -- render nothing rather than flash the page on then off */
        )}
      </main>

      {flags.profiles === true && saveAccountOpen && (
        <SaveAccountModal apiUrl={API_URL} onClose={() => setSaveAccountOpen(false)} />
      )}

      {loggedInOpen && <LoggedInModal email={profileEmail} onClose={closeLoggedIn} />}

      <SiteFooter>
        {currentPage === 'create' && (
          <>
            <br />
            <small style={{ marginTop: "0.5rem", display: "block" }}>
              Text Generated with <a href="https://inkstitch.org/" target="_blank" rel="noopener noreferrer">Ink/Stitch</a>
              {" · "}
              <a href="https://github.com/jlyon1/inkstitch-lib" target="_blank" rel="noopener noreferrer">Source</a>
            </small>
          </>
        )}
        {currentPage === 'digitize' && (
          <>
            <br />
            <small style={{ marginTop: "0.5rem", display: "block" }}>
              Digitized with <a href="https://inkstitch.org/" target="_blank" rel="noopener noreferrer">Ink/Stitch</a>
              {" · "}
              <a href="https://github.com/jlyon1/inkstitch-lib" target="_blank" rel="noopener noreferrer">Source</a>
            </small>
          </>
        )}
      </SiteFooter>
    </>
  );
}

export default App;
